import React from 'react';

import { locale } from '@trqk/utils';
import { IconX } from '@trqk/components';

const year = new Date().getFullYear();

/**
 * Tooltip that explains the difference between performance periods and statement periods.
 * @param props - {@link RenderTooltipMessageProps}
 * @returns JSX
 */
export const PerformanceVsStatementPeriods = ({
    close,
}: RenderTooltipMessageProps): JSX.Element => (
    <div className="text-tertiary-500 bg-white pb-4">
        <div className="flex justify-end px-2 pt-2">
            <IconX
                addClasses="respond-to-mouse"
                width={16}
                fillColor="tertiary-400"
                handleClick={close}
            />
        </div>
        <div className="px-8 mt-2">
            <h4 className="text-lg mb-2">
                {locale.enUS.HELP.PERFORMANCE_VS_STATEMENT_PERIODS.TITLE}
            </h4>
            <div className="text-sm space-y-4">
                <p>
                    {
                        locale.enUS.HELP.PERFORMANCE_VS_STATEMENT_PERIODS
                            .DEFINITIONS.PERFORMANCE_PERIOD.PART_1
                    }{' '}
                    <span className="font-semibold">
                        {
                            locale.enUS.HELP.PERFORMANCE_VS_STATEMENT_PERIODS
                                .DEFINITIONS.PERFORMANCE_PERIOD.PART_2
                        }
                    </span>{' '}
                    {
                        locale.enUS.HELP.PERFORMANCE_VS_STATEMENT_PERIODS
                            .DEFINITIONS.PERFORMANCE_PERIOD.PART_3
                    }{' '}
                    {
                        locale.enUS.HELP.PERFORMANCE_VS_STATEMENT_PERIODS
                            .DEFINITIONS.STATEMENT_PERIOD.PART_1
                    }{' '}
                    <span className="font-semibold">
                        {
                            locale.enUS.HELP.PERFORMANCE_VS_STATEMENT_PERIODS
                                .DEFINITIONS.STATEMENT_PERIOD.PART_2
                        }
                    </span>{' '}
                    {
                        locale.enUS.HELP.PERFORMANCE_VS_STATEMENT_PERIODS
                            .DEFINITIONS.STATEMENT_PERIOD.PART_3
                    }
                </p>
                <p>
                    <span className="font-bold">
                        {
                            locale.enUS.HELP.PERFORMANCE_VS_STATEMENT_PERIODS
                                .EXAMPLE.TITLE
                        }
                    </span>{' '}
                    {
                        locale.enUS.HELP.PERFORMANCE_VS_STATEMENT_PERIODS
                            .EXAMPLE.DESCRIPTION
                    }
                </p>
                {/*
                    Chart showing example of statement period vs. performance period
                */}
                <div className="px-2 pt-2 pb-3 border border-tertiary-200">
                    <div
                        className="grid gap-2 items-center justify-items-center"
                        style={{
                            gridTemplateColumns: '190px 90px 90px 90px 90px',
                            gridTemplateRows: '30px 30px 30px',
                        }}
                    >
                        <p
                            className="font-semibold justify-self-end pr-2"
                            style={{ gridArea: '1 / 1 / 1 / 1' }}
                        >
                            {
                                locale.enUS.HELP
                                    .PERFORMANCE_VS_STATEMENT_PERIODS.EXAMPLE
                                    .CHART.CALENDAR_QUARTER
                            }
                        </p>
                        <p
                            className="font-semibold"
                            style={{ gridArea: '1 / 2 / 1 / 2' }}
                        >
                            {
                                locale.enUS.HELP
                                    .PERFORMANCE_VS_STATEMENT_PERIODS.EXAMPLE
                                    .CHART.Q1
                            }{' '}
                            {year}
                        </p>
                        <p
                            className="font-semibold"
                            style={{ gridArea: '1 / 3 / 1 / 3' }}
                        >
                            {' '}
                            {
                                locale.enUS.HELP
                                    .PERFORMANCE_VS_STATEMENT_PERIODS.EXAMPLE
                                    .CHART.Q2
                            }{' '}
                            {year}
                        </p>
                        <p
                            className="font-semibold"
                            style={{ gridArea: '1 / 4 / 1 / 4' }}
                        >
                            {' '}
                            {
                                locale.enUS.HELP
                                    .PERFORMANCE_VS_STATEMENT_PERIODS.EXAMPLE
                                    .CHART.Q3
                            }{' '}
                            {year}
                        </p>
                        <p
                            className="font-semibold"
                            style={{ gridArea: '1 / 5 / 1 / 5' }}
                        >
                            {' '}
                            {
                                locale.enUS.HELP
                                    .PERFORMANCE_VS_STATEMENT_PERIODS.EXAMPLE
                                    .CHART.Q4
                            }{' '}
                            {year}
                        </p>
                        <p
                            className="font-semibold justify-self-end pr-2"
                            style={{ gridArea: '2 / 1 / 2 / 1' }}
                        >
                            {
                                locale.enUS.HELP
                                    .PERFORMANCE_VS_STATEMENT_PERIODS.EXAMPLE
                                    .CHART.STATEMENT_PERIOD
                            }
                        </p>
                        {/*
                            Block that shows a statement period
                        */}
                        <div
                            style={{ gridArea: '2 / 4 / 2 / 4' }}
                            className="bg-primary-100 border border-primary-500 w-full h-full"
                        />
                        <p
                            className="font-semibold justify-self-end pr-2"
                            style={{ gridArea: '3 / 1 / 3 / 1' }}
                        >
                            {
                                locale.enUS.HELP
                                    .PERFORMANCE_VS_STATEMENT_PERIODS.EXAMPLE
                                    .CHART.PERFORMANCE_PERIOD
                            }
                        </p>
                        {/*
                            Block that shows a performance period
                        */}
                        <div
                            style={{ gridArea: '3 / 2 / 3 / 2' }}
                            className="border border-primary-500 w-full h-full"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
);
